/**
 * <safe-sticky> — a sticky wrapper that stays usable when its content is taller
 * than the viewport. Plain `position: sticky` pins the top edge and hides the
 * overflow below the fold; this element tracks scroll direction so the content
 * scrolls along with you — revealing its bottom on the way down and its top on
 * the way up — then re-pins once an edge is reached.
 *
 * It only engages when the element computes to `position: sticky` (e.g. the
 * desktop breakpoint) and no-ops otherwise, so the same markup is safe on
 * mobile where the column is static. Pair it with CSS:
 *
 *   safe-sticky { position: sticky; top: <offset>; }
 */
class SafeSticky extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = new ResizeObserver(this.recalculateStyles.bind(this));
    this.checkPositionListener = this.throttledCheckPosition.bind(this);
    this.initialTop = 0;
    this.lastKnownY = 0;
    this.currentTop = 0;
    this.position = "relative";
    this.requestId = null;
    this.lastArgs = [];
    this.cleanupCallback = null;
  }

  connectedCallback() {
    this.inView(
      () => {
        window.addEventListener("scroll", this.checkPositionListener);
        this.resizeObserver.observe(this);

        this.cleanupCallback = () => {
          window.removeEventListener("scroll", this.checkPositionListener);
          this.resizeObserver.unobserve(this);
        };
      },
      { margin: "500px" }
    );
  }

  disconnectedCallback() {
    if (this.cleanupCallback) this.cleanupCallback();
  }

  recalculateStyles() {
    this.style.removeProperty("top");
    const computedStyles = getComputedStyle(this);
    this.initialTop = parseInt(computedStyles.top, 10);
    this.position = computedStyles.position;
    this.checkPosition();
  }

  checkPosition() {
    if (this.position !== "sticky") {
      return this.style.removeProperty("top");
    }

    const bounds = this.getBoundingClientRect();
    const maxTop =
      bounds.top + window.scrollY - this.offsetTop + this.initialTop;
    const minTop = this.clientHeight - window.innerHeight + 20;

    if (window.scrollY < this.lastKnownY) {
      this.currentTop -= window.scrollY - this.lastKnownY;
    } else {
      this.currentTop += this.lastKnownY - window.scrollY;
    }

    this.currentTop = Math.min(
      Math.max(this.currentTop, -minTop),
      maxTop,
      this.initialTop
    );

    this.lastKnownY = window.scrollY;
    this.style.top = `${Math.round(this.currentTop)}px`;
  }

  throttledCheckPosition(...args) {
    this.lastArgs = args;
    if (this.requestId === null) {
      this.requestId = requestAnimationFrame(() => {
        this.requestId = null;
        this.checkPosition(...this.lastArgs);
      });
    }
  }

  inView(callback, options) {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!this.cleanupCallback) {
          callback();
        }
      } else {
        if (this.cleanupCallback) {
          this.cleanupCallback();
          this.cleanupCallback = null;
        }
      }
    }, options);
    observer.observe(this);
  }
}

if (!customElements.get('safe-sticky')) {
  customElements.define('safe-sticky', SafeSticky);
}
